import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Grid,
  Typography,
  Alert,
} from "@mui/material";
import {
  CheckCircleOutline, CancelOutlined, ScheduleOutlined, Close,
  DirectionsCarOutlined, ApartmentOutlined, LocationOnOutlined,
  RouteOutlined, AccessTimeOutlined, AccountBalanceWalletOutlined,
  CancelRounded, AltRouteOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { CUSTOM_CONSTANTS } from "Constants";
import DriverApi from "Services/Driver";
import _ from "lodash";

const STATUS_COLORS = {
  COMPLETED: "#11823b",
  CANCELLED: "#d32f2f",
  CANCELED:  "#d32f2f",
  SEARCHING: "#f57c00",
  ARRIVED:   "#7b1fa2",
  RIDING:    "#1565c0",
};

function formatDate(value) {
  if (!value) return "--";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "--";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(date);
}

const TripDriver = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Modal
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [actionLoading, setActionLoading] = useState("");   // "accept" | "reject" | "regret" | ""
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");   // message de succès

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    setError("");
    dispatch(showLoader("Chargement des voyages..."));

    try {
      const res = await DriverApi.getAllDriverTrips({ type: 0 });
      const data = res?.data?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.response?.data?.message || "Erreur lors du chargement des voyages.";
      setError(msg);
      dispatch(showSnackbar({ message: msg, autoHideDuration: 3000, anchorOrigin: { vertical: "top", horizontal: "right" }, variant: "error" }));
    } finally {
      setLoading(false);
      dispatch(hideLoader());
    }
  }, [dispatch]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const openModal = (trip) => {
    setSelectedTrip(trip);
    setActionLoading("");
    setActionError("");
    setActionSuccess("");
  };

  const closeModal = () => {
    setSelectedTrip(null);
  };

  const handleAction = async (type) => {
    if (!selectedTrip || actionLoading) return;
    setActionLoading(type);
    setActionError("");

    const apiCall =
      type === "accept" ? DriverApi.acceptTrip :
      type === "reject" ? DriverApi.rejectTrip :
      DriverApi.regretTrip;

    const labels = { accept: "accepté", reject: "refusé", regret: "reporté" };

    try {
      await apiCall(selectedTrip.id);
      setActionSuccess(`Trajet ${labels[type]} avec succès.`);
      dispatch(showSnackbar({ message: `Trajet ${labels[type]} !`, autoHideDuration: 3000, anchorOrigin: { vertical: "top", horizontal: "right" }, variant: type === "reject" ? "warning" : "success" }));
      fetchTrips();
    } catch (err) {
      setActionError(err?.response?.data?.message || err?.response?.data?.error || `Erreur lors de l'action.`);
    } finally {
      setActionLoading("");
    }
  };

  const dataRows = _.map(rows, (item, index) => ({
    id:           item?.id || index,
    sno:          index + 1,
    bookingId:    item?.booking_id || "--",
    service:      item?.name || "--",
    departure:    item?.s_address || "--",
    destination:  item?.d_address || "--",
    distance:     item?.distance ? `${parseFloat(item.distance).toFixed(1)} KM` : "--",
    duration:     item?.minutes != null ? `${item.minutes} min` : "--",
    fare:         item?.estimated_fare ? `${Number(item.estimated_fare).toLocaleString("fr-FR")} ${item?.currency || CUSTOM_CONSTANTS.BASE_CURRENCY}` : "--",
    startedAt:    formatDate(item?.started_at),
    finishedAt:   formatDate(item?.finished_at),
    createdAt:    formatDate(item?.created_at),
    status:       item?.status || "--",
  }));

  const columns = [
    { field: "sno",         headerName: "N°",          flex: 0.4, minWidth: 50,  sortable: false },
    { field: "bookingId",   headerName: "Booking ID",  flex: 1.3, minWidth: 120 },
    { field: "createdAt",   headerName: "Réservé le",  flex: 1.5, minWidth: 150 },
    { field: "startedAt",   headerName: "Démarré le",  flex: 1.5, minWidth: 150 },
    { field: "finishedAt",  headerName: "Terminé le",  flex: 1.5, minWidth: 150 },
    { field: "service",     headerName: "Service",     flex: 1.1, minWidth: 110 },
    { field: "departure",   headerName: "Départ",      flex: 2,   minWidth: 180 },
    { field: "destination", headerName: "Destination", flex: 2,   minWidth: 180 },
    { field: "distance",    headerName: "Distance",    flex: 1,   minWidth: 100 },
    { field: "duration",    headerName: "Durée",       flex: 0.9, minWidth: 90  },
    {
      field: "fare",
      headerName: "Tarif estimé",
      flex: 1.2,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: "#11823b" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Statut",
      flex: 1.2,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: STATUS_COLORS[params.value] || "#555" }}>
          {params.value}
        </Typography>
      ),
    },
  ];

  return (
    <div className={classes.pages}>
      <Grid container className={classes.root}>

        {/* ── Titre ── */}
        <Grid item xs={12}>
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Mes voyages</Typography>
            <Typography variant="body2" color="text.secondary">
              Historique de vos courses et trajets
            </Typography>
          </Box>
        </Grid>

        {/* ── Barre d'outils ── */}
        <Grid container item xs={12} spacing={1} sx={{ px: 2, pb: 1 }}>
          <Grid item>
            <Button
              variant="outlined"
              sx={{ height: 36 }}
              onClick={fetchTrips}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {loading ? "Chargement..." : "Actualiser"}
            </Button>
          </Grid>
        </Grid>

        {/* ── Erreur ── */}
        {error && (
          <Grid item xs={12} sx={{ px: 2, pb: 1 }}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        {/* ── Tableau ── */}
        <Grid item xs={12}>
          <section className={classes.dataGridTable}>
            <DataGrid
              className={classes.tripgridtable}
              getRowId={(row) => row.id}
              rows={dataRows}
              columns={columns}
              pageSize={pageSize}
              page={page}
              onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
              onPageChange={(newPage) => setPage(newPage)}
              rowsPerPageOptions={[5, 10, 25, 50]}
              pagination
              autoHeight
              disableSelectionOnClick
              rowHeight={70}
              loading={loading}
              style={{ padding: "10px", cursor: "pointer" }}
              onRowClick={(params) => openModal(rows.find((r) => r.id === params.row.id) || params.row)}
            />
          </section>
        </Grid>

      </Grid>

      {/* ── Modal ── */}
      <Dialog
        open={Boolean(selectedTrip)}
        onClose={closeModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 0,
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          },
        }}
      >
        {selectedTrip && (
          <Box sx={{ p: "24px 24px 20px" }}>

            {/* ── En-tête ── */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 46, height: 46, borderRadius: "50%", background: "#edf7ee", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AltRouteOutlined sx={{ color: "#1a7f3c", fontSize: 24 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: 17, color: "#0d1b2a" }}>
                  Détails du trajet
                </Typography>
              </Box>
              <Box
                onClick={closeModal}
                sx={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid #1a7f3c", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1a7f3c", "&:hover": { background: "#edf7ee" } }}
              >
                <Close sx={{ fontSize: 16 }} />
              </Box>
            </Box>

            {/* ── Lignes d'info ── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>

              {/* Service */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: "12px", px: 1.5, py: 1.2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: "#edf7ee", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DirectionsCarOutlined sx={{ color: "#1a7f3c", fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block", lineHeight: 1 }}>Service</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0d1b2a" }}>{selectedTrip.name || "--"}</Typography>
                </Box>
              </Box>

              {/* Départ */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: "12px", px: 1.5, py: 1.2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: "#edf7ee", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <ApartmentOutlined sx={{ color: "#1a7f3c", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block", lineHeight: 1 }}>Départ</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0d1b2a", wordBreak: "break-word" }}>{selectedTrip.s_address || "--"}</Typography>
                </Box>
              </Box>

              {/* Destination */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: "12px", px: 1.5, py: 1.2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: "#edf7ee", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <LocationOnOutlined sx={{ color: "#1a7f3c", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block", lineHeight: 1 }}>Destination</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0d1b2a", wordBreak: "break-word" }}>{selectedTrip.d_address || "--"}</Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Statistiques ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 0.8, mt: 1.5, mb: 2 }}>
              {[
                { icon: <RouteOutlined sx={{ fontSize: 16, color: "#1a7f3c" }} />, label: "Distance", value: selectedTrip.distance ? `${selectedTrip.distance} KM` : "--", iconBg: "#edf7ee" },
                { icon: <AccessTimeOutlined sx={{ fontSize: 16, color: "#1a7f3c" }} />, label: "Durée", value: selectedTrip.minutes ? `${selectedTrip.minutes} min` : "--", iconBg: "#edf7ee" },
                { icon: <AccountBalanceWalletOutlined sx={{ fontSize: 16, color: "#1a7f3c" }} />, label: "Tarif estimé", value: selectedTrip.estimated_fare ? `${Number(selectedTrip.estimated_fare).toLocaleString("fr-FR")} ${selectedTrip.currency || CUSTOM_CONSTANTS.BASE_CURRENCY}` : "--", iconBg: "#edf7ee" },
                { icon: <CancelRounded sx={{ fontSize: 16, color: STATUS_COLORS[selectedTrip.status] || "#555" }} />, label: "Statut", value: selectedTrip.status || "--", iconBg: "#fff0f0", valueColor: STATUS_COLORS[selectedTrip.status] || "#555" },
              ].map(({ icon, label, value, iconBg, valueColor }) => (
                <Box key={label} sx={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: "12px", p: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: "50%", background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {icon}
                  </Box>
                  <Typography variant="caption" sx={{ color: "#9aa5b4", fontSize: 10, lineHeight: 1 }}>{label}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 11, color: valueColor || "#0d1b2a", lineHeight: 1.2 }}>{value}</Typography>
                </Box>
              ))}
            </Box>

            {/* ── Feedback action ── */}
            {actionSuccess && <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2 }}>{actionSuccess}</Alert>}
            {actionError   && <Alert severity="error"   sx={{ mb: 1.5, borderRadius: 2 }}>{actionError}</Alert>}

            {/* ── Boutons d'action ── */}
            {!actionSuccess && (
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>

                {/* Refuser */}
                <Button
                  variant="outlined"
                  disabled={Boolean(actionLoading)}
                  onClick={() => handleAction("reject")}
                  sx={{ flex: 1, borderRadius: "12px", fontWeight: 700, py: 1.3, fontSize: 13, borderColor: "#e53935", color: "#e53935", "&:hover": { background: "#fff5f5", borderColor: "#e53935" } }}
                  startIcon={actionLoading === "reject" ? <CircularProgress size={14} color="inherit" /> : <CancelOutlined />}
                >
                  {actionLoading === "reject" ? "..." : "Refuser"}
                </Button>

                {/* Reporter */}
                <Button
                  variant="outlined"
                  disabled={Boolean(actionLoading)}
                  onClick={() => handleAction("regret")}
                  sx={{ flex: 1, borderRadius: "12px", fontWeight: 700, py: 1.3, fontSize: 13, borderColor: "#f57c00", color: "#f57c00", "&:hover": { background: "#fff8f0", borderColor: "#f57c00" } }}
                  startIcon={actionLoading === "regret" ? <CircularProgress size={14} color="inherit" /> : <ScheduleOutlined />}
                >
                  {actionLoading === "regret" ? "..." : "Reporter"}
                </Button>

                {/* Accepter */}
                <Button
                  variant="contained"
                  disabled={Boolean(actionLoading)}
                  onClick={() => handleAction("accept")}
                  sx={{ flex: 1, borderRadius: "12px", fontWeight: 700, py: 1.3, fontSize: 13, background: "linear-gradient(135deg, #2e7d32, #1a7f3c)", boxShadow: "0 4px 14px rgba(30,124,60,0.35)", "&:hover": { background: "linear-gradient(135deg, #1b5e20, #155a30)" } }}
                  startIcon={actionLoading === "accept" ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutline />}
                >
                  {actionLoading === "accept" ? "..." : "Accepter"}
                </Button>

              </Box>
            )}
          </Box>
        )}
      </Dialog>

    </div>
  );
};

export default TripDriver;
